/**
 * useAdminOrders — Iter 156.10. Extracted from `AdminOrdersPage.jsx`.
 * Owns filters, fetch, create/update/delete/placed mutations.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { adminOrdersApi } from "../../lib/api";

const EMPTY_CREATE = { customer_name: "", customer_phone: "", customer_email: "", customer_address: "", order_number: "", amount: "", discount_percent: "", discount_amount: "", payment_mode: "COD", category: "", notes: "", status: "pending" };
const EMPTY_PLACED = { order_number: "", amount: "", customer_name: "", customer_phone: "" };

export default function useAdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "", category: "", salesperson_id: "",
    date_from: "", date_to: "",
    sort_by: "created_at", sort_order: "desc",
  });
  const [filterOptions, setFilterOptions] = useState({ categories: [], salespeople: [], statuses: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPlacedOrderModal, setShowPlacedOrderModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [placedForm, setPlacedForm] = useState(EMPTY_PLACED);
  const [saving, setSaving] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page: pagination.page, limit: 20 };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const response = await adminOrdersApi.getAll(params);
      setOrders(response.data.orders || []);
      setPagination((p) => ({ ...p, pages: response.data.pages || 1, total: response.data.total || 0 }));
      if (response.data.filters) setFilterOptions(response.data.filters);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleUpdateOrder = async () => {
    if (!editingOrder) return;
    try {
      await adminOrdersApi.update(editingOrder.id, editForm);
      toast.success("Order updated");
      setEditingOrder(null);
      fetchOrders();
    } catch {
      toast.error("Failed to update order");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      await adminOrdersApi.delete(orderId);
      toast.success("Order deleted");
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete order");
    }
  };

  const handleCreateOrder = async () => {
    if (!createForm.customer_name || !createForm.amount) {
      toast.error("Customer name and amount are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...createForm,
        amount: parseFloat(createForm.amount) || 0,
        discount_percent: parseFloat(createForm.discount_percent) || 0,
        discount_amount: parseFloat(createForm.discount_amount) || 0,
      };
      const res = await adminOrdersApi.create(payload);
      toast.success(`Order created: ${res.data.order_number}`);
      setShowCreateModal(false);
      setCreateForm(EMPTY_CREATE);
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create order"); }
    finally { setSaving(false); }
  };

  const handlePlacedOrder = async () => {
    if (!placedForm.order_number || !placedForm.amount) {
      toast.error("Order number and amount are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        order_number: placedForm.order_number,
        amount: parseFloat(placedForm.amount) || 0,
        customer_name: placedForm.customer_name,
        customer_phone: placedForm.customer_phone,
        payment_mode: "COD", status: "confirmed",
      };
      const res = await adminOrdersApi.create(payload);
      toast.success(`Order recorded: ${res.data.order_number}`);
      setShowPlacedOrderModal(false);
      setPlacedForm(EMPTY_PLACED);
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record order"); }
    finally { setSaving(false); }
  };

  const filteredOrders = orders.filter((o) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      o.customer_name?.toLowerCase().includes(q) ||
      o.order_number?.toLowerCase().includes(q) ||
      o.customer_phone?.includes(q)
    );
  });

  return {
    orders, filteredOrders, loading,
    filters, setFilters, filterOptions,
    showFilters, setShowFilters,
    searchQuery, setSearchQuery,
    pagination, setPagination,
    editingOrder, setEditingOrder, editForm, setEditForm,
    showCreateModal, setShowCreateModal,
    showPlacedOrderModal, setShowPlacedOrderModal,
    createForm, setCreateForm, placedForm, setPlacedForm,
    saving,
    handleUpdateOrder, handleDeleteOrder, handleCreateOrder, handlePlacedOrder,
  };
}

export const PAYMENT_MODES = ["UPI", "Card", "Net Banking", "COD", "Bank Transfer", "Special Pay", "Proforma"];
export const ORDER_STATUSES = ["pending", "confirmed", "dispatched", "delivered", "cancelled"];
export const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  dispatched: "bg-purple-100 text-purple-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

export const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount || 0);

export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
